import { Box, CircularProgress, TablePagination } from "@material-ui/core";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import { useState } from "react";
import naboService from "services/naboService";

import {
  AccordionContent,
  AccordionIconSidebar,
  AccordionLeftContent,
  AccordionRightContent,
  DataTable,
  EmptyState,
  LoadingContainer,
  OutputBatchId,
  OutputBatchTitle,
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
  StyledChip,
  StyledLinearProgress,
  TableCell,
  TableContentWrapper,
  TableHeader,
  TableHeaderCell,
  TableRow
} from "./styles";

const OutputBatchItem = ({ batchId, outputBatch }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingRefresh, setLoadingRefresh] = useState(false);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("");
  const [orderDirection, setOrderDirection] = useState("asc");

  const fetchData = async (
    currentPage = 0,
    currentRowsPerPage = 10,
    currentOrderBy = "",
    currentOrderDirection = "asc",
    isRefresh = false
  ) => {
    if (isRefresh) {
      setLoadingRefresh(true);
    } else {
      setLoading(true);
    }

    try {
      // Construir parâmetro de ordenação
      let orderByParam = "";
      if (currentOrderBy) {
        orderByParam =
          currentOrderDirection === "desc"
            ? `-${currentOrderBy}`
            : currentOrderBy;
      }

      const params = {
        page: currentPage + 1, // Backend usa página 1-indexed
        page_size: currentRowsPerPage
      };

      if (orderByParam) {
        params.order_by = orderByParam;
      }

      const response = await naboService.getBatchOutputDetails(
        batchId,
        outputBatch.batch_output_id,
        params
      );

      setData(response.data.results || []);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      setLoadingRefresh(false);
    }
  };

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
    if (isExpanded && data.length === 0) {
      fetchData(page, rowsPerPage, orderBy, orderDirection, false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchData(newPage, rowsPerPage, orderBy, orderDirection, true);
  };

  const handleChangeRowsPerPage = event => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchData(0, newRowsPerPage, orderBy, orderDirection, true);
  };

  const handleSort = column => {
    const isAsc = orderBy === column && orderDirection === "asc";
    const newDirection = isAsc ? "desc" : "asc";
    setOrderDirection(newDirection);
    setOrderBy(column);
    setPage(0);
    fetchData(0, rowsPerPage, column, newDirection, true);
  };

  const formatDateTime = dateTime => {
    if (!dateTime) return "--";
    const date = new Date(dateTime);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getPortionTypeName = portionType => {
    const types = {
      kg: "kg",
      un: "un",
      g: "g"
    };
    return types[portionType] || portionType;
  };

  const SortIcon = ({ column }) => {
    if (orderBy !== column) {
      return <Box style={{ width: "20px" }} />;
    }
    return orderDirection === "asc" ? (
      <ArrowUpwardIcon fontSize="small" style={{ color: "#587916" }} />
    ) : (
      <ArrowDownwardIcon fontSize="small" style={{ color: "#587916" }} />
    );
  };

  return (
    <StyledAccordion expanded={expanded} onChange={handleAccordionChange}>
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <AccordionIconSidebar>
          <LocalShippingIcon />
        </AccordionIconSidebar>
        <AccordionContent>
          <AccordionLeftContent>
            <OutputBatchTitle>
              ID: <OutputBatchId>{outputBatch.batch_output_id}</OutputBatchId>
            </OutputBatchTitle>
          </AccordionLeftContent>
          <AccordionRightContent>
            <StyledChip
              variant="tags"
              size="small"
              label={`${outputBatch.tags_count} ${
                outputBatch.tags_count === 1 ? "etiqueta" : "etiquetas"
              }`}
            />
            <StyledChip
              variant="tags"
              size="small"
              label={`${outputBatch.orders_count} ${
                outputBatch.orders_count === 1 ? "pedido" : "pedidos"
              }`}
            />
          </AccordionRightContent>
        </AccordionContent>
      </StyledAccordionSummary>

      <StyledAccordionDetails>
        {loading ? (
          <LoadingContainer>
            <CircularProgress style={{ color: "#A1CA4F" }} />
          </LoadingContainer>
        ) : data.length === 0 ? (
          <EmptyState>Nenhum dado disponível</EmptyState>
        ) : (
          <>
            <DataTable elevation={1}>
              <TableHeader>
                <TableHeaderCell
                  flex={1.2}
                  sortable
                  onClick={() => handleSort("order_id")}
                >
                  Pedido
                  <SortIcon column="order_id" />
                </TableHeaderCell>
                <TableHeaderCell
                  flex={1.2}
                  sortable
                  onClick={() => handleSort("sku_id")}
                >
                  SKU
                  <SortIcon column="sku_id" />
                </TableHeaderCell>
                <TableHeaderCell flex={1.2}>ID do Item</TableHeaderCell>
                <TableHeaderCell flex={0.6}>Porcionamento</TableHeaderCell>
                <TableHeaderCell
                  flex={1}
                  sortable
                  onClick={() => handleSort("qr_code")}
                >
                  QR Code
                  <SortIcon column="qr_code" />
                </TableHeaderCell>
                <TableHeaderCell
                  flex={1.2}
                  sortable
                  onClick={() => handleSort("separated_at")}
                >
                  Data/Hora de bipagem
                  <SortIcon column="separated_at" />
                </TableHeaderCell>
              </TableHeader>

              {loadingRefresh && <StyledLinearProgress />}

              <TableContentWrapper isLoading={loadingRefresh}>
                {data.map((item, index) => (
                  <TableRow key={`${item.qr_code}-${index}`}>
                    <TableCell data-label="Pedido:" flex={1.2}>
                      {item.order_id}
                    </TableCell>
                    <TableCell data-label="SKU:" flex={1.2}>
                      {item.sku_id}
                    </TableCell>
                    <TableCell data-label="ID do Item:" flex={1.2}>
                      {item.order_item_id}
                    </TableCell>
                    <TableCell data-label="Porcionamento:" flex={0.6}>
                      {`${item.purchase_value.toLocaleString()} ${getPortionTypeName(
                        item.portion_type
                      )}`}
                    </TableCell>
                    <TableCell data-label="QR Code:" flex={1}>
                      {item.qr_code}
                    </TableCell>
                    <TableCell data-label="Data/Hora de bipagem:" flex={1.2}>
                      {formatDateTime(item.separated_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableContentWrapper>
            </DataTable>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </>
        )}
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};

// Componente principal
export const OutputBatchesAccordion = ({ batchId, naboData }) => {
  if (
    !naboData ||
    !naboData.output_batches ||
    naboData.output_batches.length === 0
  ) {
    return <EmptyState>Nenhum lote de saída gerado ainda</EmptyState>;
  }

  return (
    <Box>
      {naboData.output_batches.map(outputBatch => (
        <OutputBatchItem
          key={outputBatch.batch_output_id}
          batchId={batchId}
          outputBatch={outputBatch}
        />
      ))}
    </Box>
  );
};
